import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import { unitSymbolSvg } from '../../utils/military'
import { mapState } from '../../store/mapStore'

// Deployed military unit markers (DOM markers so the designer-rendered SVGs stay
// pixel-exact) + drag-to-reposition for own units, plus HTML5 drag-and-drop
// deployment from the "Mes unités" panel onto the map.
export function useMilitaryUnits(map, mapContainer, { showMilitary, militaryUnits, onUnitClick, onUnitMoved, onUnitDropped }) {
  const militaryMarkersRef = useRef([])

  useEffect(() => {
    militaryMarkersRef.current.forEach((m) => m.remove())
    militaryMarkersRef.current = []
    if (!map.current || !showMilitary || !militaryUnits?.length) return

    militaryUnits.forEach((unit) => {
      if (!unit.position) return
      const own = unit.country_id === mapState().ownedCountryId
      const el = document.createElement('div')
      el.className = 'military-unit-marker'
      el.style.cursor = own ? 'grab' : 'pointer'
      // Plan de guerre ouvert : les attributions globales non reprises dans
      // le plan restent visibles mais estompées (_dimmed posé par App).
      if (unit._dimmed) el.style.opacity = '0.4'
      el.innerHTML = unitSymbolSvg(unit, 30)
      el.title = own ? `${unit.name} (glisser pour déplacer)` : unit.name
      el.addEventListener('click', (e) => {
        e.stopPropagation()
        if (onUnitClick) onUnitClick(unit)
      })
      // Own units: drag & drop repositioning
      const marker = new maplibregl.Marker({ element: el, anchor: 'center', draggable: own })
        .setLngLat([unit.position.lon, unit.position.lat])
        .addTo(map.current)
      if (own && onUnitMoved) {
        marker.on('dragend', () => {
          const { lng, lat } = marker.getLngLat()
          onUnitMoved(unit, { lng, lat })
        })
      }
      militaryMarkersRef.current.push(marker)
    })
    return () => {
      militaryMarkersRef.current.forEach((m) => m.remove())
      militaryMarkersRef.current = []
    }
  }, [map, showMilitary, militaryUnits, onUnitClick, onUnitMoved])

  // Drag & drop deployment: units dragged from the "Mes unités" panel are
  // dropped anywhere on the map (HTML5 DnD → unproject to world coords).
  useEffect(() => {
    const container = mapContainer.current
    if (!container || !onUnitDropped) return
    const onDragOver = (e) => {
      if ([...e.dataTransfer.types].includes('text/mil-deploy-unit')) e.preventDefault()
    }
    const onDrop = (e) => {
      const unitId = Number(e.dataTransfer.getData('text/mil-deploy-unit'))
      if (!unitId || !map.current) return
      e.preventDefault()
      const rect = container.getBoundingClientRect()
      const lngLat = map.current.unproject([e.clientX - rect.left, e.clientY - rect.top])
      onUnitDropped(unitId, lngLat)
    }
    container.addEventListener('dragover', onDragOver)
    container.addEventListener('drop', onDrop)
    return () => {
      container.removeEventListener('dragover', onDragOver)
      container.removeEventListener('drop', onDrop)
    }
  }, [map, mapContainer, onUnitDropped])
}
